import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import { INSERT_DOC_TASK_SECTION_COMMAND } from './constants';
import { InsertTaskSectionCommand } from './insert-task-section-command';
import styles from './task-section.module.css';
import { TaskPluginConfiguration } from './types';

export class TaskSection extends Plugin {
  static get requires() {
    return [Widget];
  }

  static get pluginName(): string {
    return 'TaskSection';
  }

  init() {
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add(
      INSERT_DOC_TASK_SECTION_COMMAND,
      new InsertTaskSectionCommand(this.editor),
    );
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    schema.register('taskSection', {
      allowWhere: '$block',
      isObject: true,
      allowAttributes: ['taskSection'],
    });
  }

  private defineConverters() {
    const { conversion } = this.editor;
    const renderTaskSection =
      this.editor.config.get<TaskPluginConfiguration>('taskSection').renderer;

    // NOTE:: @Asim, looks like the data-tasksection is being saved in the API
    // instead of `data-taskSection` which is the behaviour across editors.
    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: ['taskSection'],
      },
      model: (viewElement, { writer: modelWriter }) => {
        const text = viewElement.getChild(0).data;
        const taskSection =
          text == null
            ? viewElement.getAttribute('data-tasksection') ||
              viewElement.getAttribute('data-taskSection')
            : text.slice(1, -1);

        return modelWriter.createElement('taskSection', { taskSection });
      },
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'taskSection',
      view: (modelElement, { writer: viewWriter }) => {
        const taskSection = modelElement.getAttribute('taskSection');
        const { editor } = this;

        const reactWrapper = viewWriter.createUIElement(
          'span',
          {
            class: 'taskSection__react-wrapper',
          },
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderTaskSection(
              taskSection,
              domElement,
              editor.sourceElement.dataset,
            );

            return domElement;
          },
        );

        const taskSectionView = viewWriter.createContainerElement(
          'div',
          {
            class: `taskSection ${styles.section}`,
            'data-taskSection': taskSection,
            'data-cke-ignore-events': true,
          },
          [reactWrapper],
        );

        return toWidget(taskSectionView, viewWriter);
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'taskSection',
      view: (modelItem, { writer: viewWriter }) => {
        const taskSection = modelItem.getAttribute('taskSection');

        const taskSectionEl = viewWriter.createRawElement('span', {
          class: `${styles.section} ${styles.section_loading}`,
        });

        return viewWriter.createContainerElement(
          'span',
          {
            class: 'taskSection',
            'data-taskSection': taskSection,
          },
          [taskSectionEl],
        );
      },
    });
  }
}
